import { getLocalApiOrigin } from '../localApi';
import { getEffectiveSfxVolume } from './soundSettings';

// ── Web Audio API sound engine ──────────────────────────
// Single AudioContext, all SFX decoded to AudioBuffers at startup.
// Playing a sound: createBufferSource → connect GainNode → start().
// This is the standard game audio pattern (PixiJS Sound, Howler.js).
// HTML5 Audio is only used for long streaming tracks (ambience/music).

let ctx = null;
const buffers = new Map();
const lastPlayTime = new Map();
const MIN_INTERVAL_MS = 50;

function getUrl(filename) {
  return `${getLocalApiOrigin()}/game-assets/${filename}`;
}

function getCtx() {
  if (!ctx) ctx = new AudioContext();
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

async function preload(filename) {
  if (buffers.has(filename)) return;
  buffers.set(filename, null);
  try {
    const resp = await fetch(getUrl(filename));
    const ab = await resp.arrayBuffer();
    const decoded = await getCtx().decodeAudioData(ab);
    buffers.set(filename, decoded);
  } catch {
    buffers.delete(filename);
  }
}

let soundPlayedThisFrame = false;

export function playUI(filename, { volume: volOverride, debounce = MIN_INTERVAL_MS } = {}) {
  const sfxVol = getEffectiveSfxVolume();
  if (sfxVol <= 0) return;

  const now = Date.now();
  const last = lastPlayTime.get(filename) || 0;
  if (now - last < debounce) return;
  lastPlayTime.set(filename, now);

  soundPlayedThisFrame = true;
  Promise.resolve().then(() => { soundPlayedThisFrame = false; });

  // A sample that needs shaping rather than just firing goes to playVoice,
  // which already has the filters. Keeps the shaping with the sample instead
  // of at each of its call sites. Several layers means the cue is built from
  // more than one voice; a caller's volume scales that mix rather than
  // flattening the balance inside it.
  const layers = SAMPLE_DEFAULTS[filename];
  if (layers) {
    for (const { sample = filename, ...opts } of layers) {
      playVoice(sample, volOverride === undefined
        ? opts
        : { ...opts, volume: (opts.volume ?? 1) * volOverride });
    }
    return;
  }

  const variants = RANDOM_SAMPLES[filename];
  if (variants) filename = variants[Math.floor(Math.random() * variants.length)];
  const buffer = buffers.get(filename);
  if (!buffer) return;

  try {
    const ac = getCtx();
    const source = ac.createBufferSource();
    source.buffer = buffer;
    const gain = ac.createGain();
    gain.gain.value = Math.min(1, (volOverride ?? 1) * sfxVol);
    source.connect(gain).connect(ac.destination);
    source.start(0);
  } catch {}
}

// One recording of a card meeting the table, used twice by the inspector: the
// close cue is it highpassed (the paper, none of the weight), the open's body
// layer is it lowpassed (the weight, none of the paper). Same material, two
// gestures. It is a constant rather than the filename written twice so that
// repointing the close cue can't silently retune the open.
const CARD_IMPACT = 'snd-card-slide-3.ogg';

// Sound constants
export const UI = {
  CLICK:          'snd-ui-click.mp3',
  CLICK_ALT:      'snd-ui-click-2.mp3',
  CLICK_SOFT:     'snd-ui-click-soft.mp3',
  CONFIRM:        'snd-ui-confirm.mp3',
  CANCEL:         'snd-ui-cancel.mp3',
  ERROR:          'snd-ui-error.mp3',
  OPEN:           'snd-ui-open.mp3',
  CLOSE:          'snd-ui-close.mp3',
  TRANSITION:     'snd-ui-transition.mp3',
  SCROLL:         'snd-ui-scroll.mp3',
  SWIPE:          'snd-ui-swipe.mp3',
  WHOOSH:         'snd-ui-whoosh.mp3',
  // The one notification ping: a message, a like, a reply, a friend request,
  // a draft event. One marimba note in a room, per-kind mutable in Settings
  // (NOTIFICATION_SOUND_OPTIONS). Synthesised by scripts/generate-ui-chimes.js;
  // retune there. The old drum ping, snd-ui-notification.mp3, is no longer
  // played on its own; it lives on as the drum source inside MATCH_INVITE.
  NOTIFICATION_SOFT: 'snd-ui-notification-soft.mp3',
  // The herald calling you to the field: the old notification drums struck
  // at the far end of a hall, over a deep drone. Plays for a match invite,
  // a matchmaking pairing and a public pod filling; never for a match that
  // starts from an invite the player already heard. From generate-ui-chimes.js.
  MATCH_INVITE:   'snd-ui-match-invite.mp3',
  EQUIP:          'snd-ui-equip.mp3',
  UNEQUIP:        'snd-ui-unequip.mp3',
  DELETE:          'snd-ui-delete.mp3',
  PURCHASE:       'snd-ui-purchase.mp3',
  GOLD:           'snd-ui-gold.mp3',
  CHEST_OPEN:     'snd-ui-chest-open.mp3',
  LEVEL_UP:       'snd-ui-levelup.mp3',
  ACHIEVEMENT:    'snd-ui-achievement.mp3',
  FOIL_REVEAL:    'snd-ui-foil-reveal.mp3',
  UPGRADE:        'snd-ui-upgrade.mp3',
  MAIL_SEND:      'snd-ui-mail-send.mp3',
  MAIL_RECEIVE:   'snd-ui-mail-receive.mp3',
  MAIL_DELETE:    'snd-ui-mail-delete.mp3',
  MAIL_COLLECT:  'snd-ui-mail-collect.mp3',
  INTERACT:       'snd-ui-interact.mp3',
  ITEM:           'snd-ui-item.mp3',
  SLIDER:         'snd-ui-slider.mp3',
  HOVER:          'snd-ui-hover.mp3',
  DRAG_START:     'snd-ui-drag-start.mp3',
  DRAG_DROP:      'snd-ui-drag-drop.mp3',
  INSPECTOR_OPEN: 'snd-ui-inspector-open.mp3',
  // A card sliding back down, ~200ms of body under a fade (see
  // SAMPLE_DEFAULTS). The inspector is
  // opened and dismissed in well under a second at a time, and its exit
  // animation is 120ms, so the close cue has to be over before the next open
  // begins. The previous sample ran 2.4s with its energy spread evenly across
  // the whole of it, which is 20x the animation: closing one card and opening
  // the next stacked two or three overlapping tails. Reach for a longer or
  // more ceremonious sound here and that is the failure it reintroduces.
  INSPECTOR_CLOSE:CARD_IMPACT,
  TAB:            'snd-ui-click.mp3',
  PACK_OPENING:   'snd-pack-opening.mp3',
  PACK_REVEAL:    'snd-pack-reveal.mp3',
  CARD_SLIDE:     'snd-card-slide-1.ogg',
  CARD_SHOVE:     'snd-card-shove-1.ogg',
  CARD_DEAL:      'snd-card-deal-1.mp3',
  PURCHASE_PACK:  'snd-purchase-pack.mp3',
  // Raw material for the pack burst. Listed here because only the names in
  // this table are ever decoded. Anything absent silently plays nothing.
  THUNDER:        'snd-thunder-1.mp3',
  THUNDER_ALT:    'snd-thunder-2.mp3',
  MAGIC:          'snd-magic-1.mp3',
  MAGIC_ALT:      'snd-magic-2.mp3',
  MAGIC_LOW:      'snd-magic-3.mp3',
  SCRY:           'snd-scry.mp3',
  // One of five supplied alchemy recordings, picked afresh for each brew.
  BREW:           'snd-ui-brew',
  DRINK:          'snd-ui-drink',
};

const RANDOM_SAMPLES = {
  [UI.BREW]: Array.from({ length: 5 }, (_, i) => `snd-ui-alchemy-0${i + 1}.mp3`),
  [UI.DRINK]: Array.from({ length: 5 }, (_, i) => `snd-ui-alchemy-potion-0${i + 1}.mp3`),
};

// Per-sample playback defaults, used when the caller just fires the name. Ten
// call sites play INSPECTOR_OPEN and a dozen play INSPECTOR_CLOSE as a bare
// playUI(), so how they sound belongs with the sample and not restated at each
// of them. Each entry is a list of layers; `sample` defaults to the key, so a
// single-voice cue doesn't have to name itself.
//
// INSPECTOR_OPEN on its own is a click, and it is a click because of its
// attack rather than its spectrum: it rises 34dB inside one 5ms window, 75ms
// into the file, and is gone 15ms later. Under 200Hz it carries -56.6dB,
// which is to say nothing at all. Neither filtering nor pitching fixes that.
// there is no low end in the recording to lift or shift down, so the only way
// to warm it is to put a body under it. Note that this rules out playVoice's
// `attack`, which ramps from playback start and would spend itself on the
// 70ms of dead pre-roll before the transient ever arrives.
//
// CARD_IMPACT lowpassed to 700Hz is that body, and it works because its own
// transient falls at 75ms too: the two land on the same instant and fuse into
// one event instead of reading as a hit with a second hit under it. Measured
// against the bare sample the pair gains 17dB below 200Hz and loses 10dB
// above 4k, while the 500-2000Hz core that makes the cue recognisable moves
// barely 1dB. The lowpass on the main layer is what takes the edge off; it is
// deliberately gentle, since cutting further stops sounding warm and starts
// sounding muffled.
//
// The slide is two things at once: a low thud where the card lands, and the
// paper texture of it moving. Unfiltered the thud carries 6dB more energy
// than the mids, and at the size and repetition rate the inspector fires it,
// the thud is the whole of what you notice. 600Hz is where cutting it stops:
// it puts the sub-500Hz band at -44.8dB, which is where INSPECTOR_OPEN
// already sits, so the pair reads as one voice. Cutting higher keeps
// thinning it toward a click.
//
// Volume stays under INSPECTOR_OPEN's transient: opening a card announces,
// closing it only acknowledges. Untrimmed the slide is the louder of the
// two, which inverts that.
//
// The hold/release is a fade, not a shortening. The sample's body is over by
// 200ms and its floor sits at -64dB from there, but a stray second transient
// comes back up to -44dB at 550ms, and played whole that lands as an audible
// tick after the sound has already finished. Holding through 120ms leaves the
// attack untouched and the release then carries the tail down from where the
// sample's own decay left it, ending at 320ms with the stray hit cut off
// rather than faded through.
const SAMPLE_DEFAULTS = {
  [UI.INSPECTOR_OPEN]: [
    { volume: 0.85, lowpass: 3500 },
    { sample: CARD_IMPACT, volume: 0.32, lowpass: 700, hold: 0.1, release: 0.2 },
  ],
  [UI.INSPECTOR_CLOSE]: [
    { volume: 0.35, highpass: 600, hold: 0.12, release: 0.2 },
  ],
};

// ── Reverb bus ──────────────────────────────────────
// A convolver fed by whichever voices ask for it. Shared, because reverb is a
// property of the room and not of a sound. One convolution engine for all of
// them, rather than one each.
//
// Torn down once the tail has run out. A ConvolverNode goes on running its FFT
// over silence for as long as it is connected, and this is a rare, transient
// effect; there is no reason to pay for a room nobody is standing in.

const REVERB_SECONDS = 3;
let reverbNode = null;
let reverbTeardown = null;
/** The impulse survives the node. Building it is 3.2ms of synchronous work on
 *  264,600 samples, and the node is torn down between uses. So regenerating
 *  it per burst put that block on the main thread at the exact moment the
 *  effect fires, which is audible as a crackle. */
let reverbImpulse = null;

/**
 * A room, made out of noise.
 *
 * Noise under a decay envelope is the whole trick, but noise alone reads as a
 * burst of hiss. What makes it a space is that the highs go first: the one-pole
 * below closes as the tail falls, so the end of the reverb is darker than its
 * start, the way air and soft surfaces actually behave. The short gap at the
 * front is the pre-delay. A real room answers slightly late, and without it
 * the tail fuses onto the hit instead of following it.
 */
function buildImpulse(ac) {
  const rate = ac.sampleRate;
  const len = Math.max(1, Math.floor(rate * REVERB_SECONDS));
  const impulse = ac.createBuffer(2, len, rate);
  const preDelay = Math.floor(rate * 0.02);

  for (let ch = 0; ch < 2; ch++) {
    const data = impulse.getChannelData(ch);
    let lp = 0;
    for (let i = preDelay; i < len; i++) {
      const t = (i - preDelay) / (len - preDelay);
      const cutoff = 0.42 * (1 - t) ** 1.6 + 0.015;
      lp += ((Math.random() * 2 - 1) - lp) * cutoff;
      data[i] = lp * (1 - t) ** 2.4;
    }
  }
  return impulse;
}

function getReverbBus(ac) {
  if (reverbNode === null) {
    if (reverbImpulse === null || reverbImpulse.sampleRate !== ac.sampleRate) {
      reverbImpulse = buildImpulse(ac);
    }
    reverbNode = ac.createConvolver();
    reverbNode.buffer = reverbImpulse;
    reverbNode.connect(ac.destination);
  }
  clearTimeout(reverbTeardown);
  reverbTeardown = setTimeout(() => {
    try { reverbNode.disconnect(); } catch {}
    reverbNode = null;
  }, (REVERB_SECONDS + 4) * 1000);
  return reverbNode;
}

/**
 * One voice: a recording bent into a different sound.
 *
 * `playUI` fires a sample flat, which is all most of the UI wants. A layered
 * cue wants more. A scheduled start so several voices can be stacked a few
 * milliseconds apart, a playback rate so one recording can be both the weight
 * and the crack, filters to take a bright sample and make it a rumble, and an
 * envelope so a long tail can be cut short, and a send to the reverb bus.
 *
 * Shares the context and the decoded buffers with `playUI`; an undecoded name
 * plays nothing rather than throwing. `when` is an offset in seconds from now.
 *
 * @returns {AudioBufferSourceNode|null}
 */
export function playVoice(filename, {
  volume = 1, rate = 1, when = 0, pan = 0, lowpass, highpass, decay, attack = 0,
  hold, release, reverb = 0,
} = {}) {
  const sfxVol = getEffectiveSfxVolume();
  if (sfxVol <= 0) return null;
  const buffer = buffers.get(filename);
  if (!buffer) return null;

  try {
    const ac = getCtx();
    const at = ac.currentTime + Math.max(0, when);
    const source = ac.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = rate;

    const level = Math.min(1, volume * sfxVol);
    const env = ac.createGain();
    if (attack > 0) {
      env.gain.setValueAtTime(0.0001, at);
      env.gain.exponentialRampToValueAtTime(level, at + attack);
    } else {
      env.gain.setValueAtTime(level, at);
    }
    // `release` holds the sound at full level and only then lets it go;
    // `decay` starts falling at sample zero. The difference is not subtle.
    // measured on the pack burst, a 1.6s decay cost 16dB of RMS against the
    // same layers held and released, because the body of the sample is spent
    // while the ramp is already halfway down. Use `decay` for transients that
    // genuinely start dying immediately, `release` for anything with a body.
    let stopAt = null;
    if (release !== undefined) {
      const holdUntil = at + attack + (hold ?? 0);
      env.gain.setValueAtTime(level, holdUntil);
      env.gain.exponentialRampToValueAtTime(level * 0.0016, holdUntil + release);
      env.gain.setValueAtTime(0, holdUntil + release + 0.005);
      stopAt = holdUntil + release + 0.02;
    } else if (decay !== undefined) {
      // Exponential to near-zero then a hard cut: a tail that runs all the way
      // down is no longer a tail, it is a remainder.
      env.gain.exponentialRampToValueAtTime(level * 0.0016, at + attack + decay);
      env.gain.setValueAtTime(0, at + attack + decay + 0.005);
      stopAt = at + attack + decay + 0.02;
    }

    let tail = source.connect(env);
    if (highpass !== undefined) {
      const f = ac.createBiquadFilter();
      f.type = 'highpass';
      f.frequency.value = highpass;
      f.Q.value = 0.7;
      tail = tail.connect(f);
    }
    if (lowpass !== undefined) {
      const f = ac.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = lowpass;
      f.Q.value = 0.7;
      tail = tail.connect(f);
    }
    if (pan !== 0 && typeof ac.createStereoPanner === 'function') {
      const p = ac.createStereoPanner();
      p.pan.value = Math.max(-1, Math.min(1, pan));
      tail = tail.connect(p);
    }
    // Dry straight out, wet as a send. So the reverb rings on after the
    // source has been stopped, which is the point of it.
    if (reverb > 0) {
      const send = ac.createGain();
      send.gain.value = reverb;
      tail.connect(send).connect(getReverbBus(ac));
    }
    tail.connect(ac.destination);

    source.start(at);
    if (stopAt !== null) source.stop(stopAt);
    return source;
  } catch {
    return null;
  }
}

// ── Achievement herald cue ──────────────────────────
// A foreshortened quote of the claim sting: the same ACHIEVEMENT
// sample the claim overlay plays flat at 0.85, here pitched up a
// quarter (smaller, brighter. The herald's trumpet, not the
// coronation) and cut short with a release so the phrase lands
// unresolved. The full sting at claim time resolves it. MAGIC_LOW,
// low-passed to a rumble, sits underneath as the body. A touch of
// the shared reverb bus places both in the same room as the ceremony.
export function playAchievementHerald() {
  playVoice(UI.ACHIEVEMENT, { volume: 0.45, rate: 1.25, hold: 0.35, release: 0.5, reverb: 0.15 });
  playVoice(UI.MAGIC_LOW, { volume: 0.25, lowpass: 900, decay: 0.8 });
}

// ── Generic looping pulse ───────────────────────────
// Starts the named SFX as a looping source if it isn't already
// playing, and resets a stop timer to silence it `holdMs` after the
// last pulse. Designed for caller-driven "continuous" interactions
// (custom scroll-wheel resizers, custom drag handles) that aren't
// covered by the global scroll / range-input listeners below.
const sfxLoopStates = new Map();
export function pulseUISoundLoop(filename, { volume = 0.25, holdMs = 150 } = {}) {
  const buffer = buffers.get(filename);
  if (!buffer) return;
  const sfxVol = getEffectiveSfxVolume();
  if (sfxVol <= 0) return;
  let state = sfxLoopStates.get(filename);
  if (!state) {
    try {
      const ac = getCtx();
      const gain = ac.createGain();
      gain.gain.value = sfxVol * volume;
      gain.connect(ac.destination);
      const source = ac.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      source.connect(gain);
      source.start(0);
      state = { source, gain, stopTimer: null };
      sfxLoopStates.set(filename, state);
    } catch {
      return;
    }
  }
  clearTimeout(state.stopTimer);
  state.stopTimer = setTimeout(() => {
    try { state.source.stop(); } catch {}
    sfxLoopStates.delete(filename);
  }, holdMs);
}

// ── Global listeners ────────────────────────────────

let installed = false;

function handleGlobalClick(e) {
  const btn = e.target.closest?.('button, [role="button"], [role="menuitem"], [role="menuitemradio"], [role="option"], [role="tab"], summary, a[href]');
  if (!btn) return;
  if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

  const sound = btn.getAttribute('data-sound');
  if (sound === 'none') return;

  Promise.resolve().then(() => {
    if (!soundPlayedThisFrame) {
      playUI(sound || UI.CLICK, { volume: 0.7 });
    }
  });
}

// ── Scroll sound (looping Web Audio, rAF-throttled) ──
let scrollSource = null;
let scrollGain = null;
let scrollStopTimer = null;
let scrollRAF = false;

// Timestamp of the last user input that could have initiated a scroll.
// Programmatic scrolls (e.g. an auto-scroll-to-bottom on a new chat or
// log entry) fire scroll events with no preceding input. We gate the
// sound on a recent input so those don't play the scroll sfx.
let lastScrollInputTime = 0;
const SCROLL_INPUT_WINDOW_MS = 500;

function markScrollInput() {
  lastScrollInputTime = Date.now();
}

function handleGlobalScroll(e) {
  const el = e.target;
  if (!(el instanceof HTMLElement) || el === document.documentElement) return;

  // Gate on a recent user input. Without this, any programmatic
  // setTimeout/setState driven auto-scroll plays the sfx too.
  if (Date.now() - lastScrollInputTime > SCROLL_INPUT_WINDOW_MS) return;

  if (!scrollRAF) {
    scrollRAF = true;
    requestAnimationFrame(() => {
      scrollRAF = false;
      if (scrollSource) return;
      const buffer = buffers.get(UI.SCROLL);
      if (!buffer) return;
      const sfxVol = getEffectiveSfxVolume();
      if (sfxVol <= 0) return;
      const ac = getCtx();
      scrollGain = ac.createGain();
      scrollGain.gain.value = sfxVol * 0.25;
      scrollGain.connect(ac.destination);
      scrollSource = ac.createBufferSource();
      scrollSource.buffer = buffer;
      scrollSource.loop = true;
      scrollSource.connect(scrollGain);
      scrollSource.start(0);
    });
  }

  clearTimeout(scrollStopTimer);
  scrollStopTimer = setTimeout(() => {
    if (scrollSource) { try { scrollSource.stop(); } catch {} }
    scrollSource = null;
    scrollGain = null;
  }, 150);
}

// ── Slider sound (looping Web Audio) ────────────────
let sliderSource = null;
let sliderGain = null;
let sliderStopTimer = null;

function handleGlobalInput(e) {
  if (e.target?.type !== 'range') return;

  if (!sliderSource) {
    const buffer = buffers.get(UI.SLIDER);
    if (!buffer) return;
    const sfxVol = getEffectiveSfxVolume();
    if (sfxVol <= 0) return;
    const ac = getCtx();
    sliderGain = ac.createGain();
    sliderGain.gain.value = sfxVol * 0.3;
    sliderGain.connect(ac.destination);
    sliderSource = ac.createBufferSource();
    sliderSource.buffer = buffer;
    sliderSource.loop = true;
    sliderSource.connect(sliderGain);
    sliderSource.start(0);
  }

  clearTimeout(sliderStopTimer);
  sliderStopTimer = setTimeout(() => {
    if (sliderSource) { try { sliderSource.stop(); } catch {} }
    sliderSource = null;
    sliderGain = null;
  }, 120);
}

function installGlobalListeners() {
  if (installed) return;
  installed = true;
  document.addEventListener('click', handleGlobalClick, true);
  document.addEventListener('scroll', handleGlobalScroll, { capture: true, passive: true });
  document.addEventListener('input', handleGlobalInput, { capture: true, passive: true });
  // Events that could initiate a user-driven scroll. The scroll sfx
  // only plays if one of these fires shortly before the scroll event.
  document.addEventListener('wheel', markScrollInput, { capture: true, passive: true });
  document.addEventListener('keydown', markScrollInput, { capture: true, passive: true });
  document.addEventListener('touchstart', markScrollInput, { capture: true, passive: true });
  document.addEventListener('pointerdown', markScrollInput, { capture: true, passive: true });
}

export function preloadUISounds() {
  for (const filename of new Set([...Object.values(UI), ...Object.values(RANDOM_SAMPLES).flat()])) {
    // Virtual cues preload their individual recordings above.
    if (!filename.includes('.')) continue;
    preload(filename);
  }
  installGlobalListeners();
}
